import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, Title } from "rizzui";
import toast from "react-hot-toast";
import useApi from "@/hooks/useApi";
import SettingsRepository from "@/utilities/repositories/Settings";
import { GoogleDataManagerEventLog } from "@/utilities/types/GoogleDataManager";
import { TGoogleAdsAccount } from "@/utilities/types/Setting";
import { IListResponse, ListResponseData } from "@/utilities/types/requests";
import ControlledTable from "@/components/controlled-table";
import { HeaderCell } from "@/components/ui/table";

const statusOptions = ["", "PENDING", "PROCESSING", "SUCCESS", "FAILED", "SKIPPED"];
const eventOptions = ["", "SUBMIT_LEAD_FORM", "QUALIFIED_LEAD"];

const selectValue = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "value" in value) {
    return String((value as { value: unknown }).value || "");
  }
  return "";
};

function DataManagerLogsWrapper() {
  const [status, setStatus] = useState("");
  const [eventType, setEventType] = useState("");
  const [search, setSearch] = useState("");
  const [accountId, setAccountId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<GoogleDataManagerEventLog | null>(null);
  const { request, response, loading } = useApi<ListResponseData<GoogleDataManagerEventLog>>({
    request: SettingsRepository.getGoogleDataManagerLogs,
  });
  const { request: getDetail } = useApi<{ data: GoogleDataManagerEventLog }>({
    request: SettingsRepository.getGoogleDataManagerLog,
  });
  const { request: retry } = useApi<{ data: GoogleDataManagerEventLog }>({
    request: SettingsRepository.retryGoogleDataManagerLog,
  });
  const { request: getAccounts, response: accountsResponse } = useApi<{ data: TGoogleAdsAccount[] }>({
    request: SettingsRepository.getGoogleAdsAccounts,
  });

  const load = useCallback(async () => {
    try {
      const filter = {
        ...(status ? { status } : {}),
        ...(eventType ? { eventType } : {}),
        ...(accountId ? { googleAdsAccountId: accountId } : {}),
        ...(from ? { from: `${from}T00:00:00.000Z` } : {}),
        ...(to ? { to: `${to}T23:59:59.999Z` } : {}),
        ...(search ? { search } : {}),
      };
      if (from && to && from > to) {
        toast.error("From date must be before To date");
        return;
      }
      await request({
        limit: 20,
        page,
        ...(Object.keys(filter).length ? { filter } : {}),
      });
    } catch (error: any) {
      toast.error(error?.message || "Failed to load Data Manager logs");
    }
  }, [accountId, eventType, from, page, request, search, status, to]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void getAccounts(); }, [getAccounts]);

  const result = response?.data as IListResponse<GoogleDataManagerEventLog> | undefined;
  const rows = result?.items || [];

  const openDetail = useCallback(async (id: string) => {
    try {
      const detail = await getDetail(id);
      setSelected(detail.data);
    } catch (error: any) {
      toast.error(error?.message || "Failed to load log details");
    }
  }, [getDetail]);

  const retryLog = useCallback(async (id: string) => {
    try {
      await retry(id);
      toast.success("Log queued for retry");
      await load();
    } catch (error: any) {
      toast.error(error?.message || "Failed to retry log");
    }
  }, [load, retry]);

  const columns = useMemo(() => [
    { title: <HeaderCell title="Created" />, dataIndex: "createdAt", key: "createdAt", render: (value: string) => new Date(value).toLocaleString() },
    { title: <HeaderCell title="Event" />, dataIndex: "eventType", key: "eventType" },
    { title: <HeaderCell title="Status" />, dataIndex: "status", key: "status", render: (value: string, row: GoogleDataManagerEventLog) => <span className="font-medium">{value}{row.skipReason ? ` · ${row.skipReason}` : ""}</span> },
    { title: <HeaderCell title="Attempts" />, dataIndex: "attemptCount", key: "attemptCount" },
    { title: <HeaderCell title="Request ID" />, dataIndex: "requestId", key: "requestId", render: (value: string | null) => <span className="font-mono text-xs">{value || "—"}</span> },
    { title: <HeaderCell title="Actions" />, dataIndex: "id", key: "actions", align: "right", render: (id: string, row: GoogleDataManagerEventLog) => <div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => void openDetail(id)}>Details</Button>{row.manualRetryAllowed && <Button size="sm" onClick={() => void retryLog(id)}>Retry</Button>}</div> },
  ], [openDetail, retryLog]);

  return <section className="rounded-md border border-muted bg-white p-5">
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div><Title as="h5" className="text-xl md:text-2xl">Data Manager Upload Logs</Title><p className="mt-1 text-sm text-gray-500">Admin-only upload diagnostics and manual retries.</p></div>
      <Button variant="outline" onClick={() => void load()}>Refresh</Button>
    </div>
    <div className="mb-5 grid gap-3 md:grid-cols-3">
      <Select label="Status" value={status} options={statusOptions.map((value) => ({ label: value || "All statuses", value }))} onChange={(value: unknown) => { setPage(1); setStatus(selectValue(value)); }} />
      <Select label="Event type" value={eventType} options={eventOptions.map((value) => ({ label: value || "All event types", value }))} onChange={(value: unknown) => { setPage(1); setEventType(selectValue(value)); }} />
      <Input label="Search ID / user / request" value={search} onChange={(event) => setSearch(event.target.value)} />
      <Select label="Account" value={accountId} options={[{ label: "All accounts", value: "" }, ...(accountsResponse?.data || []).map((account) => ({ label: `${account.name} (${account.accountId})`, value: account.id }))]} onChange={(value: unknown) => { setPage(1); setAccountId(selectValue(value)); }} />
      <Input type="date" label="From" value={from} onChange={(event) => { setPage(1); setFrom(event.target.value); }} />
      <Input type="date" label="To" value={to} onChange={(event) => { setPage(1); setTo(event.target.value); }} />
    </div>
    <ControlledTable
      variant="modern"
      data={rows}
      isLoading={loading}
      showLoadingText
      // @ts-expect-error The shared table typing is narrower than the existing column renderers.
      columns={columns}
      paginatorOptions={{ pageSize: 20, total: result?.total || 0, current: page, onChange: (nextPage: number) => setPage(nextPage) }}
      className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-40 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_thead.rc-table-thead]:border-t-0"
    />
    <Modal isOpen={selected !== null} onClose={() => setSelected(null)} size="lg">
      {selected && <div className="p-6">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <Title as="h6">Log details</Title>
            <p className="mt-1 text-sm text-gray-500">Restricted upload diagnostics</p>
          </div>
          <Button size="sm" variant="text" onClick={() => setSelected(null)}>Close</Button>
        </div>
        <div className="space-y-2 text-sm">
          <p>Status: <span className="font-medium">{selected.status}</span></p>
          <p>Event: <span className="font-medium">{selected.eventType}</span></p>
          <p>HTTP status: <span className="font-medium">{selected.httpStatus || "—"}</span></p>
          <p>Request ID: <span className="font-mono text-xs">{selected.requestId || "—"}</span></p>
          <p>Last attempt: <span className="font-medium">{selected.lastAttemptAt ? new Date(selected.lastAttemptAt).toLocaleString() : "—"}</span></p>
          <p>Attempts: <span className="font-medium">{selected.attemptCount}</span></p>
        </div>
        <div className="mt-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-xl font-bold text-gray-900">{selected.leadName || "N/A"}</p>
              <p className="mt-1 break-all text-sm text-gray-500">{selected.leadEmail || "—"}</p>
              <p className="text-sm text-gray-500">{selected.leadPhone || "—"}</p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <span className="rounded-full bg-blue-50 px-3 py-1 font-semibold text-blue-600">{selected.leadProjectName || "Project unavailable"}</span>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-gray-700">Agent: {selected.leadAgentName || "Unassigned"}{selected.leadAgentEmail ? ` · ${selected.leadAgentEmail}` : ""}</span>
            </div>
            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500">Click ID</p>
              <p className="break-all font-mono text-xs text-gray-800">{selected.clickIdValue || selected.clickIdValueMasked || "—"}</p>
            </div>
          </div>
        </div>
        <details className="mt-4 rounded-md border border-muted bg-gray-50 p-3 text-sm">
          <summary className="cursor-pointer font-medium">Technical diagnostics</summary>
          <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap text-xs">{JSON.stringify({ requestPayload: selected.requestPayload, payloadSummary: selected.payloadSummary, responseBody: selected.responseBody, lastErrorCode: selected.lastErrorCode, lastErrorMessage: selected.lastErrorMessage }, null, 2)}</pre>
        </details>
      </div>}
    </Modal>
  </section>;
}

export default DataManagerLogsWrapper;
